import React, { useState, useRef, useEffect } from 'react';
import { Scissors, Sparkles, ChevronLeft, ChevronRight, ShieldCheck, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function BeforeAfterSlider({ onBookIntake }) {
  const { lang } = useLanguage();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [activeCaseIndex, setActiveCaseIndex] = useState(0);
  const containerRef = useRef(null);

  const cases = [
    {
      id: 1,
      title: {
        nl: "Kruin & Voorhoofdslijn Transformatie",
        en: "Natural Frontal & Crown Hair System",
        tr: "Doğal Ön Çizgi & Tepe Protez Saç Dönüşümü"
      },
      subtitle: {
        nl: "100% Onzichtbare Micro-PU Basis • Direct 10 Jaar Jonger",
        en: "100% Invisible Micro-PU Base • Instantly 10 Years Younger",
        tr: "%100 Görünmez Mikro PU Deri Taban • Anında 10 Yaş Genç Görünüm"
      },
      beforeImg: "/images/case1_before.webp",
      afterImg: "/images/case1_after.webp",
      time: "90 Min",
      technique: "Micro-PU Skin System"
    },
    {
      id: 2,
      title: {
        nl: "Skin Fade Overloop & Volle Bovenkant",
        en: "Full Top Restoration with Seamless Skin Fade",
        tr: "Komple Üst Bölge & Kusursuz Skin Fade Geçişi"
      },
      subtitle: {
        nl: "Natuurlijke Haarinplant • Naadloze Blend met Eigen Haar",
        en: "Natural Hairline • Seamless Blend with Real Sides",
        tr: "Doğal Saç Çıkış Yönü • Kendi Saçınızla Birebir Uyum"
      },
      beforeImg: "/images/case2_before.webp",
      afterImg: "/images/case2_after.webp",
      time: "90 Min",
      technique: "Swiss Lace HD Hybrid"
    }
  ];

  const currentCase = cases[activeCaseIndex] || cases[0];

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = Math.max(5, Math.min((x / rect.width) * 100, 95));
    setSliderPosition(percentage);
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleMove(e.clientX);
    }
  };

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  return (
    <section id="transformatie" className="py-16 sm:py-24 bg-slate-900 text-white relative overflow-hidden">
      
      {/* Dynamic Aurora glowing orbs in background */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full glow-aurora-amber pointer-events-none opacity-40" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full glow-aurora-emerald pointer-events-none opacity-40" />
      <div className="absolute top-3/4 left-1/2 w-80 h-80 rounded-full glow-aurora-purple pointer-events-none opacity-40" />

      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 text-xs font-black uppercase tracking-widest shadow-lg shadow-amber-500/10">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>{lang === 'nl' ? 'INTERACTIEVE TRANSFORMATIE SLIDER' : lang === 'tr' ? 'CANLI ÖNCESİ / SONRASI DÖNÜŞÜM' : 'INTERACTIVE BEFORE & AFTER SLIDER'}</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-5xl font-black text-white tracking-tight">
            {lang === 'nl' ? (
              <>Bekijk het <span className="text-amber-400 font-serif">Ongelofelijke Verschil</span></>
            ) : lang === 'tr' ? (
              <>İnanılmaz Değişimi <span className="text-amber-400 font-serif">Canlı Karşılaştırın</span></>
            ) : (
              <>Experience the <span className="text-amber-400 font-serif">Instant Transformation</span></>
            )}
          </h2>

          <p className="text-sm sm:text-base text-slate-300 font-medium">
            {lang === 'nl' 
              ? 'Sleep de gouden schaar naar links of rechts om het voor en na resultaat van 100% echt haarwerk te vergelijken.' 
              : lang === 'tr'
              ? 'Ortadaki altın makası sağa-sola kaydırarak dökük saçtan gür saç sistemine anlık geçişi deneyimleyin.'
              : 'Drag the golden scissors handle left or right to compare before & after results.'}
          </p>
        </div>

        {/* Transformation Case Selector Tabs */}
        <div className="flex justify-center gap-3 mb-8 overflow-x-auto pb-2 custom-scrollbar">
          {cases.map((c, idx) => (
            <button
              key={c.id}
              onClick={() => {
                setActiveCaseIndex(idx);
                setSliderPosition(50);
              }}
              className={`px-5 py-2.5 rounded-2xl text-xs font-black transition-all border shrink-0 flex items-center gap-2 ${
                activeCaseIndex === idx
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white border-amber-400 shadow-lg shadow-amber-500/30 scale-105'
                  : 'bg-slate-800/80 hover:bg-slate-800 border-slate-700 text-slate-300'
              }`}
            >
              <span>{lang === 'nl' ? `Dossier ${idx + 1}` : lang === 'tr' ? `Dönüşüm Örneği ${idx + 1}` : `Case ${idx + 1}`}</span>
              <span className="hidden sm:inline text-[11px] opacity-80">({c.technique})</span>
            </button>
          ))}
        </div>

        {/* The Split Screen Slider Container */}
        <div className="max-w-3xl mx-auto">
          
          <div className="relative rounded-3xl overflow-hidden border-2 border-amber-500/50 shadow-2xl shadow-amber-500/15 bg-slate-950 aspect-[1/1] sm:aspect-[4/3] select-none touch-none">
            
            <div
              ref={containerRef}
              onMouseDown={() => setIsDragging(true)}
              onMouseMove={handleMouseMove}
              onTouchMove={handleTouchMove}
              className="w-full h-full relative cursor-ew-resize overflow-hidden"
            >
              
              {/* Layer 1: AFTER Image (Background full width, focused on hair & head) */}
              <img
                src={currentCase.afterImg}
                alt="Aura Hair After Transformation"
                className="w-full h-full object-cover object-top pointer-events-none select-none absolute inset-0"
                loading="eager"
              />

              {/* Layer 2: BEFORE Image (Clipped smoothly with CSS clipPath, focused on hair & head) */}
              <div
                className="absolute inset-0 pointer-events-none select-none overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img
                  src={currentCase.beforeImg}
                  alt="Aura Hair Before Transformation"
                  className="w-full h-full object-cover object-top pointer-events-none select-none w-full h-full"
                  loading="eager"
                />
              </div>

              {/* Badges on Top Left (BEFORE) and Top Right (AFTER) */}
              <div className="absolute top-4 left-4 z-20 pointer-events-none">
                <span className="px-3.5 py-1.5 rounded-xl bg-slate-950/85 backdrop-blur-md border border-rose-500/40 text-rose-300 text-xs font-black uppercase tracking-wider shadow-lg">
                  🔴 {lang === 'nl' ? 'VOOR (KAALHEID)' : lang === 'tr' ? 'ÖNCESİ (DÖKÜLME)' : 'BEFORE'}
                </span>
              </div>

              <div className="absolute top-4 right-4 z-20 pointer-events-none">
                <span className="px-3.5 py-1.5 rounded-xl bg-slate-950/85 backdrop-blur-md border border-emerald-500/40 text-emerald-300 text-xs font-black uppercase tracking-wider shadow-lg">
                  🟢 {lang === 'nl' ? 'NA (100% ECHT HAAR)' : lang === 'tr' ? 'SONRASI (%100 DOĞAL)' : 'AFTER'}
                </span>
              </div>

              {/* The Glowing Gold Vertical Divider Line */}
              <div
                className="absolute top-0 bottom-0 z-30 pointer-events-none"
                style={{ left: `${sliderPosition}%` }}
              >
                <div className="w-1 h-full bg-gradient-to-b from-amber-300 via-amber-400 to-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.9)] relative">
                  
                  {/* Center Drag Handle Button with glowing pulse */}
                  <div className="absolute top-1/2 -translate-y-1/2 -left-5 w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 text-slate-950 flex items-center justify-center shadow-xl shadow-amber-500/60 border-2 border-white pointer-events-auto hover:scale-110 active:scale-95 transition-transform cursor-ew-resize">
                    <Scissors className="w-5 h-5 stroke-[2.5] text-slate-950" />
                  </div>

                </div>
              </div>

            </div>

          </div>

          {/* Case Description & Details Below Slider */}
          <div className="mt-6 p-6 rounded-3xl bg-slate-800/80 backdrop-blur-xl border border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
            <div className="space-y-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <span className="px-2.5 py-0.5 rounded-md bg-amber-500/20 border border-amber-400/40 text-amber-300 text-[10px] font-mono font-bold">
                  {currentCase.technique}
                </span>
                <span className="text-xs text-slate-400 font-semibold">
                  ⏱️ {currentCase.time}
                </span>
              </div>
              <h3 className="font-serif text-lg font-bold text-white">
                {currentCase.title[lang] || currentCase.title.nl}
              </h3>
              <p className="text-xs text-slate-300 font-medium">
                {currentCase.subtitle[lang] || currentCase.subtitle.nl}
              </p>
            </div>

            <button
              onClick={onBookIntake}
              className="btn-gold-sheen px-6 py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-amber-500/30 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shrink-0"
            >
              <span>{lang === 'nl' ? 'Gratis Intake Plannen' : lang === 'tr' ? 'Ücretsiz Danışmanlık Al' : 'Book Free Consultation'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

        </div>

      </div>
    </section>
  );
}
